import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Activity, Database, Server, Shield,
  AlertTriangle, CheckCircle, XCircle, Clock, Gauge, TrendingUp,
  DollarSign, Users, Package, Bell, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { API_URL } from '../../utils/apiConfig';

const StatusIndicator = ({ status }) => {
  const colors = {
    healthy: 'bg-emerald-500',
    degraded: 'bg-amber-500',
    unhealthy: 'bg-red-500',
    unknown: 'bg-slate-500',
  };
  
  const labels = {
    healthy: 'Healthy',
    degraded: 'Degraded',
    unhealthy: 'Unhealthy',
    unknown: 'Unknown',
  };
  
  return (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full ${colors[status]} ${status === 'healthy' ? '' : 'animate-pulse'}`}></div>
      <span className={`text-sm font-medium ${status === 'healthy' ? 'text-emerald-400' : status === 'degraded' ? 'text-amber-400' : status === 'unhealthy' ? 'text-red-400' : 'text-slate-400'}`}>
        {labels[status] || status}
      </span>
    </div>
  );
};

const AlertBadge = ({ severity }) => {
  const colors = {
    info: 'bg-blue-600 text-blue-100',
    warning: 'bg-amber-600 text-amber-100',
    error: 'bg-red-600 text-red-100',
    critical: 'bg-red-700 text-white animate-pulse',
  };
  
  return (
    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${colors[severity] || 'bg-slate-600'}`}>
      {severity}
    </span>
  );
};

const ServiceCard = ({ name, icon: Icon, data, color }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-slate-800 border border-slate-700 rounded-xl p-5 hover:border-${color}-600/50 transition-all`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-${color}-900/50 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${color}-400`} />
          </div>
          <h3 className="text-white font-semibold">{name}</h3>
        </div>
        <StatusIndicator status={data?.status} />
      </div>
      
      {data?.latency_ms !== undefined && (
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-slate-400">Latency</span>
          <span className={`font-medium ${data.latency_ms < 100 ? 'text-emerald-400' : data.latency_ms < 500 ? 'text-amber-400' : 'text-red-400'}`}>
            {data.latency_ms}ms
          </span>
        </div>
      )}
      
      {data?.error && (
        <p className="text-red-400 text-xs mt-2">{data.error}</p>
      )}
      
      {data?.document_counts && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <p className="text-slate-500 text-xs mb-2">Collections</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-white text-lg font-bold">{data.document_counts.users}</p>
              <p className="text-slate-500 text-xs">Users</p>
            </div>
            <div>
              <p className="text-white text-lg font-bold">{data.document_counts.wallets}</p>
              <p className="text-slate-500 text-xs">Wallets</p>
            </div>
            <div>
              <p className="text-white text-lg font-bold">{data.document_counts.authorizations}</p>
              <p className="text-slate-500 text-xs">Auths</p>
            </div>
          </div>
        </div>
      )}
      
      {data?.capacity_percent !== undefined && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-400">Pilot Capacity</span>
            <span className={`font-medium ${data.capacity_percent < 75 ? 'text-emerald-400' : data.capacity_percent < 90 ? 'text-amber-400' : 'text-red-400'}`}>
              {data.capacity_percent}%
            </span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className={`h-full ${data.capacity_percent < 75 ? 'bg-emerald-500' : data.capacity_percent < 90 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${Math.min(100, data.capacity_percent)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>${data.total_usd?.toFixed(2)}</span>
            <span>${data.pilot_ceiling_usd}</span>
          </div>
        </div>
      )}
      
      {data?.denial_rate_percent !== undefined && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-400">Denial Rate (24h)</span>
            <span className={`font-medium ${data.denial_rate_percent < 10 ? 'text-emerald-400' : data.denial_rate_percent < 25 ? 'text-amber-400' : 'text-red-400'}`}>
              {data.denial_rate_percent}%
            </span>
          </div>
          <p className="text-slate-500 text-xs">{data.checks_24h} checks • {data.denied_24h} denied</p>
        </div>
      )}
      
      {data?.error_rate_percent !== undefined && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Error Rate</span>
            <span className={`font-medium ${data.error_rate_percent < 5 ? 'text-emerald-400' : data.error_rate_percent < 15 ? 'text-amber-400' : 'text-red-400'}`}>
              {data.error_rate_percent}%
            </span>
          </div>
          <p className="text-slate-500 text-xs mt-1">{data.requests_last_hour} requests (1h)</p>
        </div>
      )}
    </motion.div>
  );
};

export default function AdminHealthView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState('');

  const getToken = () => localStorage.getItem('pecash_token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/health/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
        setLastRefresh(new Date());
        setError('');
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to fetch health data');
      }
    } catch (err) {
      setError('Network error fetching health data');
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchDashboard();
      setLoading(false);
    };
    load();
  }, [fetchDashboard]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchDashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Activity className="w-6 h-6 text-emerald-400" />
                System Health
              </h1>
              <p className="text-sm text-slate-400">Real-time monitoring & telemetry</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {dashboard?.overall_status && (
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-lg">
                <span className="text-slate-400 text-sm">Overall:</span>
                <StatusIndicator status={dashboard.overall_status} />
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="text-slate-400 text-sm flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={autoRefresh} 
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                Auto-refresh
              </label>
            </div>
            <Button 
              variant="ghost"
              onClick={fetchDashboard}
              className="text-slate-400"
              data-testid="health-refresh"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Error */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-6">
            <p className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
          </div>
        )}

        {/* Last Refresh */}
        {lastRefresh && (
          <p className="text-slate-500 text-xs mb-4">
            Last updated: {lastRefresh.toLocaleString()} {autoRefresh && '(auto-refreshing every 30s)'}
          </p>
        )}

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <ServiceCard 
            name="Database" 
            icon={Database} 
            data={dashboard?.services?.database}
            color="blue"
          />
          <ServiceCard 
            name="API Server" 
            icon={Server} 
            data={dashboard?.services?.api}
            color="purple"
          />
          <ServiceCard 
            name="Treasury" 
            icon={DollarSign} 
            data={dashboard?.services?.treasury}
            color="emerald"
          />
          <ServiceCard 
            name="Limits Engine" 
            icon={Shield} 
            data={dashboard?.services?.limits}
            color="amber"
          />
        </div>

        {/* Metrics & Alerts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Operational Metrics */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              Operational Metrics
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-xs mb-1">Authorizations Today</p>
                <p className="text-2xl font-bold text-white">{dashboard?.metrics?.authorizations?.today || 0}</p>
                <p className="text-slate-500 text-xs">{dashboard?.metrics?.authorizations?.this_week || 0} this week</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-xs mb-1">Volume Today</p>
                <p className="text-2xl font-bold text-emerald-400">${dashboard?.metrics?.volume?.today_usd || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-xs mb-1">Pending Redemptions</p>
                <p className="text-2xl font-bold text-amber-400">{dashboard?.metrics?.authorizations?.pending_redemption || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-xs mb-1">Completed</p>
                <p className="text-2xl font-bold text-emerald-400">{dashboard?.metrics?.authorizations?.completed || 0}</p>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-700">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-white font-bold">{dashboard?.metrics?.users?.senders || 0}</p>
                  <p className="text-slate-500 text-xs">Supporters</p>
                </div>
                <div>
                  <p className="text-white font-bold">{dashboard?.metrics?.users?.merchants || 0}</p>
                  <p className="text-slate-500 text-xs">Merchants</p>
                </div>
                <div>
                  <p className="text-white font-bold">{dashboard?.metrics?.support?.open_cases || 0}</p>
                  <p className="text-slate-500 text-xs">Support Cases</p>
                </div>
                <div>
                  <p className="text-white font-bold">{dashboard?.metrics?.risk?.new_alerts || 0}</p>
                  <p className="text-slate-500 text-xs">Risk Alerts</p>
                </div>
              </div>
            </div>
          </div>

          {/* System Alerts */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-400" />
              System Alerts ({dashboard?.alerts?.length || 0})
            </h3>
            
            {dashboard?.alerts?.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {dashboard.alerts.map((alert) => (
                  <div key={alert.id} className="bg-slate-900 rounded-lg p-3 border-l-4 border-l-red-500">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium text-sm">{alert.title}</span>
                      <AlertBadge severity={alert.severity} />
                    </div>
                    <p className="text-slate-400 text-xs">{alert.message}</p>
                    <p className="text-slate-600 text-xs mt-1">{alert.category}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-400 mb-2" />
                <p className="text-emerald-400 font-medium">All Clear</p>
                <p className="text-slate-500 text-sm">No active system alerts</p>
              </div>
            )}
          </div>
        </div>

        {/* Financial Invariants */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" />
            Financial Invariants
            {dashboard?.invariants?.all_passed ? (
              <span className="ml-2 px-2 py-1 bg-emerald-900/50 text-emerald-400 text-xs rounded">ALL PASSED</span>
            ) : (
              <span className="ml-2 px-2 py-1 bg-red-900/50 text-red-400 text-xs rounded animate-pulse">ISSUES DETECTED</span>
            )}
          </h3>
          
          <div className="grid grid-cols-5 gap-4 mb-4">
            <div className="text-center p-3 bg-slate-900 rounded-lg">
              <p className="text-2xl font-bold text-white">
                {dashboard?.invariants?.checks_passed || 0}/{dashboard?.invariants?.total_checks || 0}
              </p>
              <p className="text-slate-500 text-xs">Checks Passed</p>
            </div>
            
            {/* Individual check indicators */}
            {[
              { name: 'Settlement Amounts', desc: 'No negative settlements' },
              { name: 'Orphan Auths', desc: 'All auths have valid supporters' },
              { name: 'Treasury Match', desc: 'Held balance matches pending auths' },
              { name: 'Pilot Ceiling', desc: 'Under $200 system limit' },
            ].map((check, i) => {
              const issue = dashboard?.invariants?.issues?.find(iss => 
                iss.invariant?.toLowerCase().includes(check.name.toLowerCase().replace(' ', '_'))
              );
              const passed = !issue;
              
              return (
                <div key={i} className={`text-center p-3 rounded-lg ${passed ? 'bg-emerald-900/20 border border-emerald-700/30' : 'bg-red-900/20 border border-red-700/30'}`}>
                  {passed ? (
                    <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-400 mx-auto mb-1" />
                  )}
                  <p className={`text-xs font-medium ${passed ? 'text-emerald-400' : 'text-red-400'}`}>{check.name}</p>
                  <p className="text-slate-600 text-xs">{check.desc}</p>
                </div>
              );
            })}
          </div>
          
          {/* Issues List */}
          {dashboard?.invariants?.issues?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <h4 className="text-red-400 text-sm font-medium mb-2">Issues Detected:</h4>
              <div className="space-y-2">
                {dashboard.invariants.issues.map((issue, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-white font-medium">{issue.invariant}:</span>{' '}
                      <span className="text-slate-400">{issue.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/admin/risk')}
            className="flex items-center justify-between p-4 bg-slate-800 border border-slate-700 rounded-xl hover:border-red-600/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-white">Risk Dashboard</span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-red-400" />
          </button>
          
          <button
            onClick={() => navigate('/admin/support')}
            className="flex items-center justify-between p-4 bg-slate-800 border border-slate-700 rounded-xl hover:border-emerald-600/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-emerald-400" />
              <span className="text-white">Support Cases</span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400" />
          </button>
          
          <button
            onClick={() => navigate('/admin/limits')}
            className="flex items-center justify-between p-4 bg-slate-800 border border-slate-700 rounded-xl hover:border-amber-600/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <Gauge className="w-5 h-5 text-amber-400" />
              <span className="text-white">Limits</span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-amber-400" />
          </button>
          
          <button
            onClick={() => navigate('/admin/settlements')}
            className="flex items-center justify-between p-4 bg-slate-800 border border-slate-700 rounded-xl hover:border-cyan-600/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-cyan-400" />
              <span className="text-white">Settlements</span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400" />
          </button>
        </div>
      </main>
    </div>
  );
}
